/**
 * BranchManager Component
 *
 * Main management interface for locations and their contacts.
 * Implements the tabbed interface following ContactPartnerManager pattern.
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useLocalDeleteBranch } from '@/hooks/useLocalBranch';
import {
  useLocalBranchContacts,
  useLocalCreateBranchContact,
  useLocalUpdateBranchContact,
} from '@/hooks/useLocalBranchContact';
import { useFormsTranslation } from '@/hooks/useTranslation';
import type {
  Branch,
  BranchFormData,
  BranchManagerProps,
  CombinedBranchFormData,
} from '@/types/branch.types';
import type {
  BranchContact,
  BranchContactFormData,
} from '@/types/branchContact.types';
import {
  Building,
  Clock,
  Edit,
  MapPin,
  MoreHorizontal,
  Plus,
  Trash2,
  Users,
} from 'lucide-react';
import React, { useState } from 'react';
import BranchContactDialog from './BranchContactDialog';
import BranchContactManager from './BranchContactManager';
import BranchDialog from './BranchDialog';
import BranchFormTabs from './BranchFormTabs';

const BranchManager: React.FC<BranchManagerProps> = ({
  branches,
  onAdd,
  onEdit,
  onDelete,
  isLoading = false,
  token,
  onSubmit,
}) => {
  const [_searchTerm, _setSearchTerm] = useState('');
  const [branchDialogOpen, setBranchDialogOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<BranchContact | null>(
    null
  );
  const { t } = useFormsTranslation();

  // Fetch data using hooks
  const deleteBranchMutation = useLocalDeleteBranch();
  const { data: contactsData } = useLocalBranchContacts();
  const createContactMutation = useLocalCreateBranchContact();
  const updateContactMutation = useLocalUpdateBranchContact();

  // Get contact count for tabs
  const displayContacts = Array.isArray(contactsData?.data)
    ? contactsData.data
    : [];
  const filteredContacts = displayContacts.filter(
    contact =>
      `${contact.firstName} ${contact.lastName}`
        .toLowerCase()
        .includes(_searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(_searchTerm.toLowerCase()) ||
      contact.function.toLowerCase().includes(_searchTerm.toLowerCase())
  );

  // Filter locations based on search term
  const filteredBranches = branches.filter(
    (branch: Branch) =>
      branch.name.toLowerCase().includes(_searchTerm.toLowerCase()) ||
      branch.city.toLowerCase().includes(_searchTerm.toLowerCase())
  );

  // Location handlers
  const handleAddBranch = () => {
    setEditingBranch(null);
    setBranchDialogOpen(true);
  };

  const handleEditBranch = (branch: Branch) => {
    setEditingBranch(branch);
    setBranchDialogOpen(true);
  };

  const handleDeleteBranch = async (branchId: string) => {
    if (window.confirm(t('branch.list.confirmDelete'))) {
      if (onDelete) {
        onDelete(branchId);
      } else {
        await deleteBranchMutation.mutateAsync(branchId);
      }
    }
  };

  const handleSaveBranch = (data: BranchFormData) => {
    if (editingBranch) {
      onEdit(editingBranch.id, data);
    } else {
      onAdd(data);
    }
    setBranchDialogOpen(false);
    setEditingBranch(null);
  };

  // Contact handlers
  const handleAddContact = () => {
    setEditingContact(null);
    setContactDialogOpen(true);
  };

  const handleSaveContact = async (data: BranchContactFormData) => {
    try {
      if (editingContact) {
        // Update existing contact
        await updateContactMutation.mutateAsync({
          id: editingContact.id,
          data,
        });
      } else {
        // Create new contact
        await createContactMutation.mutateAsync(data);
      }
      setContactDialogOpen(false);
      setEditingContact(null);
    } catch (error) {
      console.error('Error saving location contact:', error);
    }
  };

  // Helper function to get contacts for a specific location
  const getContactsForBranch = (branchId: string): BranchContact[] => {
    return displayContacts.filter((contact: BranchContact) => {
      // Handle both old (branchId) and new (branchIds) data formats
      if (contact.branchIds && Array.isArray(contact.branchIds)) {
        return contact.branchIds.includes(branchId);
      }
      // Fallback for old data format (should not happen after migration)
      return false;
    });
  };

  // Helper function to handle adding contact from location row
  const handleAddContactForBranch = (_branchId: string) => {
    // TODO: Pre-select the location when opening the dialog
    setEditingContact(null);
    setContactDialogOpen(true);
  };

  // Helper function to get opening status
  const getOpeningStatus = (_branch: Branch) => {
    // This is a simplified check - would need more complex logic for real implementation
    return t('branch.list.status.closed'); // Placeholder
  };

  // Validation functions for footer navigation
  const handleValidateBranches = async (): Promise<boolean> => {
    // For demo purposes, consider valid if there's at least one location
    const isValid = branches.length > 0;
    return isValid;
  };

  const handleValidateContacts = async (): Promise<boolean> => {
    // For demo purposes, consider valid if there's at least one contact
    const isValid = filteredContacts.length > 0;
    return isValid;
  };

  // Submit function for footer navigation
  const handleSubmitForm = async () => {
    // If we have a token and onSubmit handler, use it
    if (token && onSubmit) {
      const formData: CombinedBranchFormData = {
        branches,
        branchContacts: filteredContacts,
      };
      await onSubmit(formData);
      return;
    }

    // Fallback to simulation for development
    await new Promise(resolve => setTimeout(resolve, 2000));
    alert('¡Formulario enviado exitosamente! (Simulación)');
  };

  // Calculate validation states
  const branchesValidationState = branches.length > 0 ? 'valid' : 'invalid';
  const contactsValidationState =
    filteredContacts.length > 0 ? 'valid' : 'invalid';

  return (
    <Card className='w-full'>
      <CardContent>
        <BranchFormTabs
          branches={filteredBranches}
          contacts={filteredContacts} // Pass contact count for badge
          onAddBranch={handleAddBranch}
          onAddContact={handleAddContact}
          // Footer navigation props
          branchesValidationState={branchesValidationState}
          contactsValidationState={contactsValidationState}
          onValidateBranches={handleValidateBranches}
          onValidateContacts={handleValidateContacts}
          onSubmit={handleSubmitForm}
          isSubmitting={false} // TODO: Connect to actual loading state
        >
          {{
            branches: (
              <div className='rounded-md border'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('branch.list.headers.name')}</TableHead>
                      <TableHead>{t('branch.list.headers.address')}</TableHead>
                      <TableHead>{t('branch.list.headers.contacts')}</TableHead>
                      <TableHead>{t('branch.list.headers.status')}</TableHead>
                      <TableHead>{t('branch.list.headers.language')}</TableHead>
                      <TableHead className='text-right'>
                        {t('branch.list.headers.actions')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBranches.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className='h-24 text-center'>
                          {_searchTerm
                            ? t('branch.list.emptyStates.noSearchResults')
                            : t('branch.list.emptyStates.noBranches')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredBranches.map(branch => (
                        <TableRow key={branch.id}>
                          <TableCell className='font-medium'>
                            <div className='flex items-center gap-2'>
                              <Building className='h-4 w-4 text-muted-foreground' />
                              {branch.name}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className='flex items-center gap-1 text-sm text-muted-foreground'>
                              <MapPin className='h-3 w-3' />
                              {branch.street} {branch.number}, {branch.zipCode}{' '}
                              {branch.city}
                            </div>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const branchContacts = getContactsForBranch(
                                branch.id
                              );
                              const contactCount = branchContacts.length;

                              if (contactCount === 0) {
                                return (
                                  <div className='flex items-center gap-2'>
                                    <Users className='h-4 w-4 text-muted-foreground' />
                                    <span className='text-sm text-muted-foreground'>
                                      {t('branch.list.noContacts')}
                                    </span>
                                    <Button
                                      variant='ghost'
                                      size='sm'
                                      className='h-7 px-2'
                                      onClick={() =>
                                        handleAddContactForBranch(branch.id)
                                      }
                                    >
                                      <Plus className='h-3 w-3 mr-1' />
                                      {t('branch.list.addContact')}
                                    </Button>
                                  </div>
                                );
                              }

                              if (contactCount === 1) {
                                const contact = branchContacts[0];
                                return (
                                  <div className='flex items-center gap-2'>
                                    <Users className='h-4 w-4 text-muted-foreground' />
                                    <span className='text-sm'>
                                      {contact.firstName} {contact.lastName}
                                    </span>
                                  </div>
                                );
                              }

                              // Multiple contacts - show counter and tooltip
                              const firstContact = branchContacts[0];
                              return (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className='flex items-center gap-2 cursor-help'>
                                        <Users className='h-4 w-4 text-muted-foreground' />
                                        <Badge
                                          variant='secondary'
                                          className='font-normal'
                                        >
                                          {contactCount}
                                        </Badge>
                                        <span className='text-sm'>
                                          {firstContact.firstName}{' '}
                                          {firstContact.lastName}
                                        </span>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent className='space-y-1'>
                                      <div className='font-semibold text-xs'>
                                        {t('branch.list.allContacts')}:
                                      </div>
                                      {branchContacts.map(contact => (
                                        <div
                                          key={contact.id}
                                          className='text-xs'
                                        >
                                          • {contact.firstName}{' '}
                                          {contact.lastName}
                                        </div>
                                      ))}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <div className='flex items-center gap-2'>
                              <Clock className='h-3 w-3' />
                              <Badge variant='outline'>
                                {getOpeningStatus(branch)}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant='secondary'>
                              {branch.language.toUpperCase()}
                            </Badge>
                          </TableCell>
                          <TableCell className='text-right'>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant='ghost' className='h-8 w-8 p-0'>
                                  <MoreHorizontal className='h-4 w-4' />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align='end'>
                                <DropdownMenuItem
                                  onClick={() => handleEditBranch(branch)}
                                >
                                  <Edit className='mr-2 h-4 w-4' />
                                  {t('branch.list.actions.edit')}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleDeleteBranch(branch.id)}
                                  className='text-destructive'
                                >
                                  <Trash2 className='mr-2 h-4 w-4' />
                                  {t('branch.list.actions.delete')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            ),
            contacts: (
              <BranchContactManager
                searchTerm={_searchTerm}
                onContactDialogOpenChange={setContactDialogOpen}
                onEditingContactChange={setEditingContact}
              />
            ),
          }}
        </BranchFormTabs>

        {/* Dialogs */}
        <BranchDialog
          open={branchDialogOpen}
          onOpenChange={setBranchDialogOpen}
          branch={editingBranch || undefined}
          onSave={handleSaveBranch}
          isLoading={isLoading}
        />

        <BranchContactDialog
          open={contactDialogOpen}
          onOpenChange={setContactDialogOpen}
          branchContact={editingContact || undefined}
          onSave={handleSaveContact}
          isLoading={
            createContactMutation.isPending || updateContactMutation.isPending
          }
        />
      </CardContent>
    </Card>
  );
};

export default BranchManager;
